import Image from "next/image";
import Link from "next/link";
import { cn } from "../utils/className";

interface ButtonLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  children: React.ReactNode;
  backgroundColorClass?: string;
  href?: string;
  textClassName?: string;
  className?: string;
  size?: "small" | "medium" | "large";
}

export const ButtonLink = ({
  children,
  backgroundColorClass,
  href,
  size = "medium",
  className,
  textClassName,
  ...props
}: ButtonLinkProps) => {
  const hasBackgroundColor = !!backgroundColorClass;

  const buttonContent = (
    <>
      {hasBackgroundColor && (
        <span
          className={cn(
            "w-4 h-4 absolute top-1/2 left-8 transform -translate-y-1/2 rounded-full group-hover:scale-[60] transition-transform ease-in-out duration-300",
            {
              "w-3 h-3": size === "small",
              "w-4 h-4": size === "medium",
            },
            backgroundColorClass
          )}
        ></span>
      )}
      <span
        className={cn(
          "text-slate-600 relative z-10 transition-colors ease-in-out duration-300 whitespace-nowrap",
          {
            "group-hover:text-white": hasBackgroundColor,
          },
          textClassName
        )}
      >
        {children}
      </span>
      <Image
        src="/images/arrow-right.svg"
        alt="Arrow Right"
        width={53}
        height={24}
        className={cn(
          "color-button__arrow filter w-10 h-auto absolute right-8 object-contain opacity-0 group-hover:opacity-100 transition-opacity ease-in-out duration-300",
          {
            invert: hasBackgroundColor,
          }
        )}
        draggable={false}
      />
    </>
  );

  const buttonClassName = cn(
    `group relative overflow-hidden flex items-center justify-center gap-2.5 bg-white  px-16 hover:pl-12 hover:pr-20 py-3 lg:py-3.5 rounded-full 
  font-medium cursor-pointer transition-padding duration-300`,
    className
  );

  // If href is provided, use Link component
  if (href) {
    return (
      <Link className={buttonClassName} href={href} {...props}>
        {buttonContent}
      </Link>
    );
  }

  // If no href, use button element
  return (
    <button
      className={buttonClassName}
      onClick={
        (props.onClick as unknown as React.MouseEventHandler<HTMLButtonElement>) ||
        (() => {})
      }
    >
      {buttonContent}
    </button>
  );
};
